import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

/** Render plain text with very light formatting:
 * - double-newline → paragraph
 * - lines starting with `## ` → h2
 * - lines starting with `### ` → h3
 * - lines starting with `> ` → blockquote
 * Keeps things safe (no HTML injection) while letting Jesse format easily. */
function PostBody({ body }) {
  if (!body) return null;
  const blocks = body.replace(/\r\n/g, "\n").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="prose prose-lg max-w-none">
      {blocks.map((block, i) => {
        const key = `${i}-${block.slice(0, 24)}`;
        if (block.startsWith("### ")) return <h3 key={key} className="font-serif text-2xl text-ink mt-10 mb-3">{block.slice(4)}</h3>;
        if (block.startsWith("## ")) return <h2 key={key} className="font-serif text-3xl text-ink mt-12 mb-4">{block.slice(3)}</h2>;
        if (block.startsWith("> ")) {
          return (
            <blockquote key={key} className="border-l-2 border-navy/40 pl-5 my-6 italic text-ink-muted">
              {block.slice(2)}
            </blockquote>
          );
        }
        return <p key={key} className="text-ink-muted text-lg leading-[1.75] mb-5 whitespace-pre-wrap">{block}</p>;
      })}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="bg-cream min-h-screen">
      <Navbar />

      {loading ? (
        <div className="py-32 flex items-center justify-center text-ink-soft">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      ) : error || !post ? (
        <div className="py-32 text-center px-6">
          <h1 className="font-serif text-3xl text-ink mb-3">Post not found</h1>
          <p className="text-ink-soft mb-6">It may have been moved or unpublished.</p>
          <Link to="/blog" className="text-navy hover:underline">← Back to journal</Link>
        </div>
      ) : (
        <article className="max-w-[760px] mx-auto px-6 lg:px-10 pt-16 pb-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-navy mb-10">
            <ArrowLeft size={14} /> Back to journal
          </Link>
          <div className="text-[11px] uppercase tracking-[0.32em] text-ink-soft mb-4">
            {formatDate(post.published_at)} · {post.author || "Jesse Winter"}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink leading-[1.1] mb-6">{post.title}</h1>
          {post.excerpt && (
            <p className="text-ink-muted text-xl leading-relaxed border-l-2 border-sand pl-4 italic mb-10">{post.excerpt}</p>
          )}
          {post.cover_image_path && (
            <img
              src={`${API}/blog-cover/${post.id}`}
              alt={post.title}
              className="w-full rounded-3xl mb-10 aspect-[16/9] object-cover"
            />
          )}
          <PostBody body={post.body} />
        </article>
      )}

      <Footer />
    </div>
  );
}
